import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ImpersonationContext = createContext(null);

const IMPERSONATION_STORAGE_KEY = 'r2yn_impersonation_state';

export function ImpersonationProvider({ children }) {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedVendor, setImpersonatedVendor] = useState(null);
  const [impersonationToken, setImpersonationToken] = useState(null);
  const [adminSession, setAdminSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore impersonation state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      if (savedState) {
        const state = JSON.parse(savedState);
        setIsImpersonating(state.isImpersonating);
        setImpersonatedVendor(state.impersonatedVendor);
        setImpersonationToken(state.impersonationToken);
        setAdminSession(state.adminSession);
      }
    } catch (error) {
      console.error('Failed to restore impersonation state:', error);
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  const startImpersonation = useCallback(async (vendorId, vendorName) => {
    try {
      // Save current admin session
      const currentToken = localStorage.getItem('r2yn_token');
      const currentUser = localStorage.getItem('r2yn_user');
      
      if (!currentToken || !currentUser) {
        toast.error('No admin session found');
        return false;
      }

      const adminSessionData = {
        token: currentToken,
        user: JSON.parse(currentUser)
      };

      // Call impersonation endpoint
      const response = await api.post(`/admin/vendors/${vendorId}/impersonate`);
      const { access_token, vendor, message } = response.data;

      // Replace current token with impersonation token
      localStorage.setItem('r2yn_token', access_token);
      
      // Update user object to vendor
      const vendorUser = {
        id: vendor.owner_user_id || vendorId,
        role: 'vendor',
        email: vendor.email || '',
        name: vendorName
      };
      localStorage.setItem('r2yn_user', JSON.stringify(vendorUser));

      // Persist impersonation state to localStorage
      const impersonationState = {
        isImpersonating: true,
        impersonatedVendor: { id: vendorId, name: vendorName, ...vendor },
        impersonationToken: access_token,
        adminSession: adminSessionData
      };
      localStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(impersonationState));

      // Update React state
      setImpersonationToken(access_token);
      setImpersonatedVendor({ id: vendorId, name: vendorName, ...vendor });
      setAdminSession(adminSessionData);
      setIsImpersonating(true);

      toast.success(message || `Now viewing as ${vendorName}`);
      
      // Navigate to vendor dashboard
      window.location.href = '/vendor';
      
      return true;
    } catch (error) {
      console.error('Impersonation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to impersonate vendor');
      return false;
    }
  }, []);

  const exitImpersonation = useCallback(() => {
    if (!adminSession) {
      toast.error('Cannot exit impersonation: No admin session found');
      return;
    }

    // Restore admin session
    localStorage.setItem('r2yn_token', adminSession.token);
    localStorage.setItem('r2yn_user', JSON.stringify(adminSession.user));

    // Clear impersonation state from localStorage
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);

    // Clear React state
    setIsImpersonating(false);
    setImpersonatedVendor(null);
    setImpersonationToken(null);
    setAdminSession(null);

    toast.success('Exited impersonation mode');
    
    // Force page reload to refresh all contexts
    window.location.href = '/admin/vendors';
  }, [adminSession]);

  if (loading) {
    return null; // or a loading spinner
  }

  return (
    <ImpersonationContext.Provider 
      value={{ 
        isImpersonating, 
        impersonatedVendor, 
        startImpersonation, 
        exitImpersonation 
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
}
